import './styles/theme.css';
import './styles/colors.css';
import { useApiClient } from './api/ApiClientContext';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';

import { Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import CallbackHandler from './pages/CallbackHandler';
import WelcomePage from './pages/welcomePage/WelcomePage';
import LoginPage from './components/LoginPage';
import { useHybridAuth } from './HybridAuthProvider';
import TermsGuard from './components/TermsGuard';
import LeaderboardPage from './pages/leaderboardPage/LeaderboardPage';
import Header from './components/header/Header';
import AboutProgramPage from './pages/aboutProgramPage/AboutProgramPage';
import NoAccount from './components/NoAccount';
import TenantNotAuthorized from './components/TenantNotAuthorized';
import DebugPage from './pages/DebugPage';
import TeamsHello from './pages/TeamsHello';
import SummaryPage from './pages/summaryPage/SummaryPage';
import InitiativeSummaryPage from './pages/initiativeSummaryPage/InitiativeSummaryPage';
import InitiativeDetailsPage from './pages/initiativeDetailsPage/InitiativeDetailsPage';
import AchievementsPage from './pages/achievementsPage/AchievementsPage';
import ProfilePage from './pages/profilePage/ProfilePage';

// Main app content with MSAL auth flow
const AppContent = () => {
  const { isAuthenticated, isLoading, user, logout, login } = useHybridAuth();
  const [authError, setAuthError] = useState<string | null>(null);
  const [displayStep, setDisplayStep] = useState(1); // What the header shows
  const [showStepper, setShowStepper] = useState(false);
  const [clickedStep, setClickedStep] = useState<number | null>(null); // Step clicked by user
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isEmbedMode = searchParams.get('embed') === 'true';
  const isInitiativeDetailsPage = location.pathname === '/initiative-details';

  const apiClient = useApiClient();
  const {
    data: userFlowStatus,
    isError: userFlowIsError,
  } = useQuery({
    queryKey: ['userFlowStatus'],
    queryFn: async () => {
      try {
        const res = await apiClient.api.getUserFlowStatusApiV1AuthUserFlowStatusGet();
        return res?.data;
      } catch (error: any) {
        console.log('Auth error caught:', error);

        if (error?.status === 401) {
          const errorDetail = error?.error?.detail || '';
          console.log('401 error detail:', errorDetail);

          if (errorDetail === 'User not found') {
            console.log('User not found in database');
            setAuthError('no_account');
          } else if (errorDetail === 'Tenant not authorized') {
            console.log('Tenant not authorized');
            setAuthError('tenant_not_authorized');
          } else if (errorDetail === 'Token expired') {
            console.log('Token expired, triggering login for token refresh');
            login();
          } else {
            console.log('Invalid token, triggering logout');
            logout();
          }
        }
        throw error;
      }
    },
    enabled: isAuthenticated && !isLoading,
    retry: false,
  });

  // Fetch current user profile for job_title
  const { data: currentUserProfile } = useQuery({
    queryKey: ['currentUserProfile'],
    queryFn: async () => {
      const res = await apiClient.api.getCurrentUserProfileApiV1AuthMeGet();
      return res?.data;
    },
    enabled: isAuthenticated && !isLoading,
  });

  const isOnboarding = userFlowStatus?.navigation_path === 'onboarding';
  const isPlatformOnboarding = userFlowStatus?.navigation_path === 'platform_onboarding';

  // Handle auth errors
  useEffect(() => {
    if (userFlowIsError && authError) {
      console.log('Auth error detected:', authError);
    }
  }, [userFlowIsError, authError]);

  const handleStepChange = (step: number, shouldShowStepper: boolean) => {
    // Only update display if no user click is pending
    if (clickedStep === null) {
      setDisplayStep(step);
    }
    setShowStepper(shouldShowStepper);
  };

  const handleStepClick = (step: number) => {
    setClickedStep(step);
    setDisplayStep(step);
    
    setTimeout(() => {
      setClickedStep(null);
    }, 100);
  };

  if (isLoading) return <div>Loading...</div>;
  if (!isAuthenticated) return <LoginPage />;

  // Handle authentication errors - must check after isAuthenticated
  if (authError === 'no_account') {
    return <NoAccount userEmail={user?.email} />;
  }

  if (authError === 'tenant_not_authorized') {
    return <TenantNotAuthorized userEmail={user?.email} />;
  }

  // If React Query has an error but authError not set, still loading/checking
  if (userFlowIsError && !authError) {
    return <div>Checking authorization...</div>;
  }

  return (
    <TermsGuard>
      {!isEmbedMode && (
        <Header
          isOnboarding={isOnboarding || isPlatformOnboarding}
          currentStep={displayStep}
          showStepper={showStepper}
          onStepClick={handleStepClick}
          user={user ? { name: user.name || user.email || 'User', email: user.email, jobTitle: currentUserProfile?.user?.job_title, avatarUrl: currentUserProfile?.profile?.avatar_url } : { name: 'User' }}
          logout={logout}
          isLeader={userFlowStatus?.app_role?.is_leader}
          isSponsor={userFlowStatus?.app_role?.is_sponsor}
          hideNav={isInitiativeDetailsPage || isPlatformOnboarding}
        />
      )}
      <Routes>
        <Route path="/callback" element={<CallbackHandler />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/" element={<WelcomePage userFlowStatus={userFlowStatus} onStepChange={handleStepChange} stepClickTrigger={clickedStep} />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/about" element={<AboutProgramPage />} />
        <Route path="/summary" element={<SummaryPage />} />
        <Route path="/initiative-summary" element={<InitiativeSummaryPage />} />
        <Route path="/initiative-details" element={<InitiativeDetailsPage />} />
        <Route path="/achievements" element={<AchievementsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/debug" element={<DebugPage />} />
      </Routes>
    </TermsGuard>
  );
};

// Root App component - Teams routes bypass MSAL auth flow
const App = () => {
  return (
    <Routes>
      {/* Teams routes handle their own auth (Teams SSO) - bypass normal MSAL flow */}
      <Route path="/teams-hello" element={<TeamsHello />} />
      {/* All other routes go through normal MSAL auth */}
      <Route path="/*" element={<AppContent />} />
    </Routes>
  );
};

export default App;
